import type { MemberStatus, OrgMemberResponse } from './types';

/** Checklist order, and the only list of statuses the filter knows about. */
export const MEMBER_STATUS_ORDER: readonly MemberStatus[] = [
  'active',
  'not_invited',
  'pending',
  'expired',
  'deactivated',
];

export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
  active: 'Active',
  not_invited: 'Not invited',
  pending: 'Pending invite',
  expired: 'Expired invite',
  deactivated: 'Deactivated',
};

/** The badge text, shorter than the filter's labels. */
export const MEMBER_STATUS_SHORT_LABELS: Record<MemberStatus, string> = {
  active: 'Active',
  not_invited: 'Not invited',
  pending: 'Pending',
  expired: 'Expired',
  deactivated: 'Deactivated',
};

/** The status badge palette for the members grid. */
export const MEMBER_STATUS_BADGE_STYLES: Record<MemberStatus, string> = {
  active: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  not_invited: 'border-dashed border-slate-400 bg-white text-slate-600',
  pending: 'border-blue-300 bg-blue-50 text-blue-800',
  expired: 'border-amber-300 bg-amber-50 text-amber-800',
  deactivated: 'border-slate-300 bg-slate-100 text-slate-600',
};

/**
 * Opens on the people who can still be scheduled. Every other state is one
 * somebody goes looking for on purpose rather than reads past.
 */
export const DEFAULT_MEMBER_STATUS_FILTER: readonly MemberStatus[] = ['active'];

/**
 * The members grid's two views. Running the team and lining up people to
 * invite are different jobs, so a row belongs to exactly one of them and the
 * status filter only ever offers that view's statuses.
 */
export type MemberView = 'team' | 'not_invited';

export const MEMBER_VIEW_STATUSES: Record<MemberView, readonly MemberStatus[]> =
  {
    team: ['active', 'pending', 'expired', 'deactivated'],
    not_invited: ['not_invited'],
  };

/** What each view opens on: the people that job is about right now. */
export const DEFAULT_VIEW_STATUS_FILTER: Record<
  MemberView,
  readonly MemberStatus[]
> = {
  team: ['active', 'pending'],
  not_invited: ['not_invited'],
};

export function memberView(member: OrgMemberResponse): MemberView {
  return MEMBER_VIEW_STATUSES.not_invited.includes(memberStatus(member))
    ? 'not_invited'
    : 'team';
}

export const MEMBER_STATUS_OPTIONS = MEMBER_STATUS_ORDER.map(status => ({
  value: status,
  label: MEMBER_STATUS_LABELS[status],
}));

export function memberStatusOptions(view: MemberView) {
  return MEMBER_STATUS_OPTIONS.filter(option =>
    MEMBER_VIEW_STATUSES[view].includes(option.value)
  );
}

/**
 * The one state a member is in for the filter's purposes. Deactivation wins
 * over a pending invitation: their access is gone either way, and listing them
 * under an invitation nobody can now accept would be a lie.
 */
export function memberStatus(member: OrgMemberResponse): MemberStatus {
  if (member.deactivated_at) return 'deactivated';
  if (member.invite_status === 'not_invited') return 'not_invited';
  if (!member.is_pending) return 'active';
  // A pending invitation that has not lapsed carries no invite_status, so an
  // absent value is the pending case rather than an unknown one.
  return member.invite_status === 'expired' ? 'expired' : 'pending';
}

/**
 * Whether this row is somebody with a membership here. Kinnser staff not
 * invited yet have a user record but no membership, so everything a
 * membership holds (titles, credentials, clinics, roles) stays read-only until
 * they are invited.
 */
export function isAgencyMember(member: OrgMemberResponse): boolean {
  return !!member.roger_user_id && member.invite_status !== 'not_invited';
}

/**
 * Whether this row's name and phone can be edited. Those live on the user
 * record, so a member and a Kinnser person not invited yet both qualify:
 * any row with a user record.
 */
export function canEditProfile(member: OrgMemberResponse): boolean {
  return !!member.roger_user_id;
}
